<script setup lang="ts">
import type { Property } from '~/core/entities/property';

import { EventBusKeys } from '~/core/helpers/symbols';
import { getPropertyImageAlt } from '~/utils';

import { StandardSlider } from '~/components/StandardSlider';
import { Skeleton } from '~/components/ui/skeleton';

import PropertyPhotoCarouselGridItemDeprecated from './PropertyPhotoCarouselGridItem/GridItem.vue';
import PropertyPhotoCarouselSingleItemDeprecated from './PropertyPhotoCarouselSingleItem/SingleItem.vue';

interface CarouselItem {
	type: 'single' | 'grid';
	photo?: { watermarkUrl: string; url?: string };
	photos?: Array<{ watermarkUrl: string; url?: string }>;
	index?: number;
	startIndex?: number;
}

interface StandardSliderExposed {
	currentCarouselItemIndex: number;
	scrollableSlidesNumber: number;
	scrollTo: (index: number, immediate?: boolean) => void;
	reinitializeCarousel: (() => void) | undefined;
}

const props = withDefaults(
	defineProps<{
		property: Property | null;
		images: Array<{ watermarkUrl: string; url?: string }>;
		isLoading?: boolean;
		sectionName?: import('~/types/mixpanel').SectionName;
	}>(),
	{
		isLoading: false,
		sectionName: 'hero section'
	}
);

const emit = defineEmits<{
	'photo-clicked': [
		payload: {
			photoIndex: number;
			photoUrl: string;
			clickType: 'photo' | 'cta';
		}
	];
}>();

const standardSliderRef =
	useTemplateRef<StandardSliderExposed>('standardSliderRef');

const carouselItemSize = computed<'half-seamless'>(() => 'half-seamless');

const carouselItems = computed<CarouselItem[]>(() => {
	if (!props.images || props.images.length === 0) return [];

	const items: CarouselItem[] = [];

	if (props.images[0]) {
		items.push({
			type: 'single',
			photo: props.images[0],
			index: 0
		});
	}

	if (props.images.length >= 4) {
		items.push({
			type: 'grid',
			photos: props.images.slice(1, 4),
			startIndex: 1
		});
	}

	const remainingStartIndex = props.images.length >= 4 ? 4 : 1;
	for (let index = remainingStartIndex; index < props.images.length; index++) {
		items.push({
			type: 'single',
			photo: props.images[index],
			index
		});
	}

	return items;
});

const currentPhotoCount = computed(() => {
	if (!props.images || props.images.length === 0) return 1;

	const currentIndex = standardSliderRef.value?.currentCarouselItemIndex ?? 0;

	if (currentIndex === 0) {
		return Math.min(4, props.images.length);
	}

	const photosFromFirstPage = Math.min(4, props.images.length);
	const remainingPhotos = Math.max(0, props.images.length - 4);

	if (remainingPhotos === 0) return photosFromFirstPage;

	const additionalPhotos = Math.min(currentIndex, remainingPhotos);
	return photosFromFirstPage + additionalPhotos;
});

function getImageAlt(index: number | null) {
	return getPropertyImageAlt(
		props.property?.propertyType?.name,
		props.property?.suites,
		props.property?.region,
		props.property?.city,
		props.property?.askingPrice,
		index
	);
}

function handlePhotoClick(
	photoIndex: number,
	photoUrl: string,
	clickType: 'photo' | 'cta' = 'photo'
) {
	emit('photo-clicked', {
		photoIndex,
		photoUrl,
		clickType
	});
}

const desktopCarouselCountBus = useEventBus(
	EventBusKeys.desktopHeroSectionPhotoCount
);

watch(
	currentPhotoCount,
	newValue => {
		desktopCarouselCountBus.emit(newValue);
	},
	{ immediate: true }
);
</script>

<template>
	<div class="hidden size-full xl:block">
		<StandardSlider
			ref="standardSliderRef"
			:items="carouselItems"
			:is-loading="isLoading"
			:section-name="sectionName"
			:slides-to-scroll="1"
			:carousel-options="{
				cyclic: true,
				align: 'start',
				class: 'size-full',
				carouselWrapperClasses: 'size-full'
			}"
			:show-navigation-arrows="true"
			:display-title="false"
			:display-footer="false"
			:limit-photos="false"
			carousel-content-class="h-full !gap-1 lg:gap-1"
			:carousel-item-size="carouselItemSize"
			container-class="!p-0 size-full"
			main-container-class="!max-w-none size-full"
			class="size-full"
		>
			<template #items="{ item, index }">
				<div class="size-full">
					<PropertyPhotoCarouselSingleItemDeprecated
						v-if="item.type === 'single' && item.photo"
						:photo="item.photo"
						:photo-index="item.index ?? 0"
						:is-loading="isLoading"
						:alt-text="getImageAlt(item.index ?? 0)"
						:image-props="{
							preload: index === 0,
							fetchpriority: index === 0 ? 'high' : 'auto',
							decoding: 'async',
							loading: index === 0 ? 'eager' : 'lazy'
						}"
						@photo-clicked="handlePhotoClick"
					/>

					<PropertyPhotoCarouselGridItemDeprecated
						v-if="
							item.type === 'grid' &&
							item.photos &&
							item.startIndex !== undefined
						"
						:photos="item.photos"
						:start-index="item.startIndex"
						:is-loading="isLoading"
						:get-image-alt="getImageAlt"
						:image-props="{
							preload: false,
							decoding: 'async'
						}"
						@photo-clicked="handlePhotoClick"
					/>
				</div>
			</template>

			<template #skeleton>
				<div class="size-full space-y-1">
					<Skeleton class="size-full" variant="white" :is-active="true" />
				</div>
			</template>
		</StandardSlider>
	</div>
</template>
